import React, { useCallback, useState } from "react";
import GridItem from "../../components/Grid/GridItem.js";
import GridContainer from "../../components/Grid/GridContainer.js";
import { useHistory } from "react-router-dom";
import Dialog from '@material-ui/core/Dialog';
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Button from "../../components/CustomButtons/Button.js";
import themeStyle from "../../assets/jss/eclipse/components/themeStyle.js";
import { loginToApp } from "../../redux-services/actions/actions.js";
import { ReactComponent as AccountReady } from "../../assets/img/account_ready.svg"
import { primaryColor, primaryIconColor } from "../../style.scss";
import { DCustomerManagementApi } from "eclipse-react-sdk/services";
import { SpinnerComponent } from "react-element-spinner";
import AlertNotification from "../../components/Snackbar/AlertNotification.js";
import { WalletStatus } from "eclipse-react-sdk/services/models";
import { v4 as uuidv4 } from 'uuid';
import { ReactComponent as BiometricIcon } from '../../assets/img/BiometricLight.svg'
import { ReactComponent as BiometricSuccessIcon } from '../../assets/img/BiometricSuccess.svg'
import { getLoginChallenge, loginWithWebAuthn, verifyLoginChallenge } from '../../views-auth/Login/webauthn/webauthn.helper'
import { platformAuthenticatorIsAvailable, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { ReactComponent as BiometricErrorIcon } from '../../assets/img/biometric-error-icon.svg'

const useStyles = makeStyles(themeStyle);

export function RatifyKYCSuccess(props) {
  const template = props?.appData?.template;
  const pwaAppOptions = template?.pwaAppOptions;
  const identityPrefix = template?.identityPrefix;
  const userIdentity = `${identityPrefix}WEBAUTH-${props?.appData?.identity}`;
  const createWallet = pwaAppOptions?.createWallet?.allow || 0;
  const walletTitleText = pwaAppOptions?.walletTitleText;
  const isCreateDefaultDigitalWallet = pwaAppOptions?.defaultCreateDigitalWallet;
  const passwordPINText = pwaAppOptions?.passwordPINText;
  const history = useHistory();
  const classes = useStyles();
  const [isWebAuthNAvailable, setIsWebAuthNAvailable] = useState(false);
  const [openWebAuthnDialog, setOpenWebAuthnDialog] = useState(false);
  const [openWebAuthnSuccessDialog, setOpenWebAuthnSuccessDialog] = useState(false);
  const [isLoading, setLoading] = React.useState(false);
  const [walletGenerated, setWalletGenerated] = useState(false);
  const [alertData, setAlert] = useState(null);
  const [openWebAuthnErrorDialog, setOpenWebAuthnErrorDialog] = useState(false);
  const backToHome = useCallback(() => {
    const appData = {
      ...props?.appData,
      isRefresh: true
    }
    props.ratifyKYCSuccessHandler(appData);
    history.push({
      pathname: '/pages/dashboard'
    });
  }, [props, history])

  React.useEffect(() => {
    (async () => {
      setIsWebAuthNAvailable(await platformAuthenticatorIsAvailable());
    })();
  })

  React.useEffect(() => {
    return () => {
      window.onpopstate = () => {
        backToHome();
      }
    };
  }, [history, backToHome]);

  const showAlert = (isAlertDisplay, alertType, alertMessage) => {
    setAlert({ isAlertDisplay, alertType, alertMessage })
    setTimeout(() => {
      setAlert(null);
    }, 3000);
  }

  const redirectToErrorScreen = useCallback((fromScreen, toScreen) => {
    history.push({
      pathname: toScreen,
      state: { fromScreen }
    });
  }, [history]);

  const createDefaultDigitalWallet = useCallback(() => {
    const customerWalletAPI = new DCustomerManagementApi();
    const createWalletBody = {
      externalUniqueId: uuidv4(),
      name: `My ${walletTitleText}`,
      description: `Default Created ${walletTitleText} Post KYC Success`,
      status: WalletStatus?.ACTIVE,
      walletTypeId: props?.appData?.template?.publicCloseLoopWalletTypeId
    }
    customerWalletAPI.tenantsTenantIdCustomersCustomerIdWalletsPost(props?.appData?.auth?.tenantId, props?.appData?.customer?.customerId, createWalletBody).then(() => {
      setWalletGenerated(true);
      setLoading(false);
    }).catch((error) => {
      setLoading(false);
      if (error instanceof Error && error.response && error.response.data && error.response.data.length > 0 && error.response.data[0].description) {
        showAlert(true, 'ERROR', `${error.response.data[0].code} :: ${error.response.data[0].description}`);
      } else {
        redirectToErrorScreen('/pages/dashboard', '/pages/error');
      }
    });
  }, [props, redirectToErrorScreen, walletTitleText]);

  const getWallets = useCallback(() => {
    setLoading(true);
    const customerWalletAPI = new DCustomerManagementApi();
    customerWalletAPI.tenantsTenantIdCustomersCustomerIdWalletsGet(props?.appData?.auth?.tenantId, props?.appData?.customer?.customerId).then((customerWalletAPIResponse) => {
      let digitalWalletList = [];
      if (customerWalletAPIResponse.data && customerWalletAPIResponse.data.length > 0) {
        const activeWallets = customerWalletAPIResponse?.data?.filter(wallet => wallet?.status !== WalletStatus?.CANCELLED) || [];
        digitalWalletList = activeWallets?.filter(wallet => wallet?.walletTypeId === props?.appData?.template?.publicCloseLoopWalletTypeId);
      }
      if (digitalWalletList && digitalWalletList.length < 1 && isCreateDefaultDigitalWallet) {
        createDefaultDigitalWallet();
        return;
      }
      setWalletGenerated(true);
      setLoading(false);
    }).catch((error) => {
      setLoading(false);
      if (error instanceof Error && error.response && error.response.data && error.response.data.length > 0 && error.response.data[0].description) {
        showAlert(true, 'ERROR', `${error.response.data[0].code} :: ${error.response.data[0].description}`);
      } else {
        redirectToErrorScreen('/pages/dashboard', '/pages/error');
      }
    });
  }, [props, redirectToErrorScreen, createDefaultDigitalWallet, isCreateDefaultDigitalWallet]);

  const handleWebAuthnDialog = () => {
    if ((isWebAuthNAvailable && browserSupportsWebAuthn())) {
      setOpenWebAuthnDialog(true);
    } else {
      backToHome();
    }
  }
  const initWebAuthn = async () => {
    await getLoginChallenge(userIdentity)
      .then(async (challenge) => {
        await verifyLoginChallenge(challenge).then(async (result) => {
          setOpenWebAuthnDialog(false);
          setLoading(true);
          await loginWithWebAuthn({ ...result, identity: userIdentity }).then(() => {
            setOpenWebAuthnSuccessDialog(true);
          }).catch(error => {
            setLoading(false);
            if (error instanceof Error && error.response && error.response.data && error.response.data.length > 0 && error.response.data[0].description) {
              showAlert(true, 'ERROR', `${error.response.data[0].code} :: ${error.response.data[0].description}`);
            } else {
              redirectToErrorScreen(`/pages/user`, `error`);
            }
          });
        }).catch(() => {
          setOpenWebAuthnErrorDialog(true);
        })
      }).catch(() => {
        setOpenWebAuthnErrorDialog(true);
      });
  }

  React.useEffect(() => {
    if (!walletGenerated) {
      getWallets();
    }
  }, [getWallets, walletGenerated])
  return (
    <div>
      <SpinnerComponent loading={isLoading} position="global" color={primaryColor} />
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
              <AccountReady className={classes.screenCenterPrimaryIcon} fill={primaryIconColor}
                stroke={primaryIconColor} />
              <h1 className='auth-title' style={{ margin: '50px 0 0 0' }}>You're account <br /> is ready!</h1>
              {createWallet > 0 ?
                <p className={classes.regularFontSize}><b>Next you can add your {walletTitleText?.toLowerCase()} and
                  cards.</b></p>
                : <p className={classes.regularFontSize}><b>Securely add your card to pay QR codes, buy airtime or data
                  and pay bills.</b></p>
              }
            </GridItem>
            <GridItem xs={12} sm={12} md={12}>
              <Button className={classes.buttonRadius} size="lg" block color="primary" onClick={handleWebAuthnDialog}
                type="button">Done</Button>
            </GridItem>
          </GridContainer>
        </GridItem>
      </GridContainer>
      {alertData?.isAlertDisplay ? <AlertNotification alertData={alertData} /> : null}

      {/* Dialog to ask for Biometric registration */}
      <Dialog open={openWebAuthnDialog}>
        <div style={{ float: 'left', width: '100%', padding: '20px', boxSizing: 'border-box' }}>
          <p className={classes.regularFontSize + ' ' + classes.textCenter + ' m-t-0'}><span><BiometricIcon /></span></p>
          <p className={classes.regularFontSize + ' ' + classes.textCenter + ' m-t-0'}><b>Use your fingerprint or face scan to sign in</b></p>
          <p className={classes.regularFontSize + ' ' + classes.textCenter + ' m-t-0'}>Would you like to use your biometric authentication on this device to sign in faster?</p>
          <GridContainer>
            <GridItem xs={6} sm={6} md={6}>
              <Button className={classes.buttonRadius} size="lg" block color="transparent" onClick={backToHome}
                type="button">No thanks</Button>
            </GridItem>
            <GridItem xs={6} sm={6} md={6}>
              <Button className={classes.buttonRadius} size="lg" block color="primary" onClick={initWebAuthn}
                type="button">Use biometrics</Button>
            </GridItem>
          </GridContainer>
        </div>
      </Dialog>
      <Dialog open={openWebAuthnErrorDialog}>
        <div style={{ float: 'left', width: '100%', padding: '20px', boxSizing: 'border-box' }}>
          <p className={classes.regularFontSize + ' ' + classes.textCenter + ' m-t-0'}>
            <span><BiometricErrorIcon /></span>
          </p>
          <p className={classes.regularFontSize + ' ' + classes.textCenter + ' m-t-0'}>
            <b>We can't verify your identity</b>
          </p>
          <p className={classes.regularFontSize + ' ' + classes.textCenter + ' m-t-0'}>
            Your device's biometrics can't verify your identity.You can signing in with your {passwordPINText}.
          </p>
          <span style={{ display: 'flex', justifyContent: 'flex-end', margin: '20px 0 0 0' }}>
            <strong className={classes.linkColor} onClick={backToHome}>OK</strong> &nbsp;&nbsp;
          </span>
        </div>
      </Dialog>
      <Dialog open={openWebAuthnSuccessDialog}>
        <div style={{ float: 'left', width: '100%', padding: '20px', boxSizing: 'border-box' }}>
          <p className={classes.regularFontSize + ' ' + classes.textCenter + ' m-t-0'}><span><BiometricSuccessIcon /></span></p>
          <p className={classes.regularFontSize + ' ' + classes.textCenter + ' m-t-0'}><b>You can now sign in using your biometrics on this device</b></p>
          <p className={classes.regularFontSize + ' ' + classes.textCenter + ' m-t-0'}>You can manage your biometric sign-in in your profile.</p>
          <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
              <Button className={classes.buttonRadius} size="lg" block color="primary" onClick={backToHome}
                type="button">Done</Button>
            </GridItem>
          </GridContainer>
        </div>
      </Dialog>

    </div>
  );
}

const mapStateToProps = state => ({
  appData: state.applicationData.appData
})

const mapDispatchToProps = dispatch => ({
  ratifyKYCSuccessHandler: data => dispatch(loginToApp(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(RatifyKYCSuccess)